<template>
  <div v-if="allItems.length" v-viewer="{ filter: viewerFilter }" :class="wrapperClass">
    <img
      v-for="(item, index) in allItems"
      :key="item.key"
      :src="item.src"
      :class="[imgClass, { 'oss-media-thumbs-hidden': isOverflowThumb(index) }]"
      :style="isOverflowThumb(index) ? null : mergedImgStyle"
      @error="onImgError(item.stable)"
    />
    <span
      v-if="moreCount > 0"
      class="oss-media-thumbs-more"
      @click.stop="openOverflowPreview"
    >+{{ moreCount }}</span>
  </div>
</template>

<script>
import {
  parseMediaFilesList,
  batchResolveOssMediaUrls,
  stripOssUrlQuery,
  isAliyunOssHttpsUrl,
  resolveMediaUrl,
  ossMediaDebugLog,
  lookupCachedOssSignedUrl,
} from '../utils/mediaUrl'
import { getUploadSchoolId } from '../utils/ossMediaUpload'

/**
 * 列表多图缩略图 + v-viewer 预览（OSS 按需预签名）。
 * max 仅限制可见缩略图数量；超出图隐藏仍参与预览，点 +N 可从溢出第一张打开。最后更新：2026-10-27
 */
export default {
  name: 'OssMediaThumbs',
  props: {
    media: { type: String, default: '' },
    schoolId: { type: [String, Number], default: '' },
    /** 缩略图宽度，如 48px / 60px */
    thumbWidth: { type: String, default: '60px' },
    /** 缩略图高度；不传则与宽度相同（正方形） */
    thumbHeight: { type: String, default: '' },
    max: { type: Number, default: 0 },
    wrapperClass: { type: String, default: 'oss-media-thumbs' },
    imgClass: { type: String, default: '' },
    imgStyle: { type: [String, Object], default: '' },
  },
  data() {
    return {
      srcMap: {},
      /** 加载失败的稳定 url，统一隐藏裂图图标 */
      failedMap: {},
    }
  },
  computed: {
    urls() {
      return parseMediaFilesList(this.media)
    },
    moreCount() {
      if (this.max <= 0) return 0
      return Math.max(0, this.urls.length - this.max)
    },
    /**
     * 全部可预览项（含超出 max 的隐藏图），保证 v-viewer 能翻完整组。
     */
    allItems() {
      const items = []
      this.urls.forEach((url, index) => {
        const stable = stripOssUrlQuery(url)
        if (this.failedMap[stable]) return
        const src = this.getDisplaySrc(url)
        if (!src) return
        items.push({
          key: index + '-' + stable,
          stable,
          src,
        })
      })
      return items
    },
    /** 合成固定尺寸样式，保证列表行高一致 */
    mergedImgStyle() {
      const height = this.thumbHeight || this.thumbWidth
      const base = {
        width: this.thumbWidth,
        height,
        objectFit: 'cover',
        display: 'block',
        cursor: 'pointer',
        borderRadius: '4px',
        verticalAlign: 'middle',
      }
      if (!this.imgStyle) return base
      if (typeof this.imgStyle === 'string') {
        return Object.assign({}, base, this.parseStyleString(this.imgStyle))
      }
      return Object.assign({}, base, this.imgStyle)
    },
  },
  watch: {
    media: {
      immediate: true,
      handler() {
        this.failedMap = {}
        this.resolveAll()
      },
    },
    schoolId() {
      this.failedMap = {}
      this.resolveAll()
    },
  },
  methods: {
    /** max 之外的缩略图仍进 DOM，但不占列表位 */
    isOverflowThumb(index) {
      return this.max > 0 && index >= this.max
    },
    /** v-viewer 只收集 img，忽略 +N 等非图片节点 */
    viewerFilter(image) {
      return image && image.tagName === 'IMG'
    },
    /** 点击 +N：打开溢出第一张（没有则打开第一张） */
    openOverflowPreview() {
      const root = this.$el
      if (!root) return
      const hidden = root.querySelector('img.oss-media-thumbs-hidden')
      if (hidden) {
        hidden.click()
        return
      }
      const first = root.querySelector('img')
      if (first) first.click()
    },
    /** 图片加载失败时隐藏该图，避免浏览器默认裂图 */
    onImgError(stable) {
      if (!stable || this.failedMap[stable]) return
      this.$set(this.failedMap, stable, true)
    },
    /** 解析 "a:b;c:d" 为对象，便于与默认样式合并 */
    parseStyleString(raw) {
      const out = {}
      String(raw).split(';').forEach((part) => {
        const idx = part.indexOf(':')
        if (idx <= 0) return
        const key = part.slice(0, idx).trim()
        const val = part.slice(idx + 1).trim()
        if (!key) return
        const camel = key.replace(/-([a-z])/g, (_, c) => c.toUpperCase())
        out[camel] = val
      })
      return out
    },
    getDisplaySrc(url) {
      const stable = stripOssUrlQuery(url)
      if (this.srcMap[stable]) return this.srcMap[stable]
      if (!isAliyunOssHttpsUrl(stable)) {
        return resolveMediaUrl(stable)
      }
      return ''
    },
    async resolveAll() {
      const map = {}
      const localUrls = []
      this.urls.forEach((url) => {
        const stable = stripOssUrlQuery(url)
        if (!isAliyunOssHttpsUrl(stable)) {
          map[stable] = resolveMediaUrl(stable)
          localUrls.push(stable)
        }
      })

      const schoolId = this.schoolId || getUploadSchoolId(this)
      const ossUrls = this.urls
        .map((u) => stripOssUrlQuery(u))
        .filter((u) => isAliyunOssHttpsUrl(u))
      const pendingOss = []
      ossUrls.forEach((stable) => {
        const hit = schoolId ? lookupCachedOssSignedUrl(schoolId, stable, true) : null
        if (hit) map[stable] = hit
        else pendingOss.push(stable)
      })
      this.srcMap = map

      ossMediaDebugLog('OssMediaThumbs', {
        localCount: localUrls.length,
        ossCount: ossUrls.length,
        cacheHit: ossUrls.length - pendingOss.length,
        pending: pendingOss.length,
        schoolId: schoolId || null,
      })

      if (!schoolId || !pendingOss.length) return

      try {
        const batch = await batchResolveOssMediaUrls(pendingOss, schoolId, { inline: true })
        this.srcMap = { ...this.srcMap, ...batch }
        ossMediaDebugLog('OssMediaThumbs完成', {
          resolved: Object.keys(this.srcMap).length,
          ossSigned: Object.keys(batch).length,
        })
      } catch (e) {
        console.warn('[OssMediaThumbs]', e)
      }
    },
  },
}
</script>

<style scoped>
.oss-media-thumbs {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 4px;
}
.oss-media-thumbs-more {
  font-size: 12px;
  color: #909399;
  cursor: pointer;
  user-select: none;
}
.oss-media-thumbs-hidden {
  position: absolute !important;
  width: 0 !important;
  height: 0 !important;
  opacity: 0 !important;
  overflow: hidden !important;
  pointer-events: none;
  margin: 0 !important;
  padding: 0 !important;
  border: 0 !important;
}
</style>
